import * as React from 'react';
import { Link } from 'react-router-dom';
import { KeyRound } from 'lucide-react';

import { Badge } from '../ui/badge';
import { EmptyState } from '../ui/empty-state';
import { BarrePagination } from '../ui/pagination-bar';
import { usePagination } from '../../hooks/usePagination';
import { cn } from '../../lib/utils';
import { PastillePrevu } from './TableauRapport';
import { BarreEmpilee, Chiffre, EncartSignalements, Legende, Montant, PastilleSignalement } from './VisuelsRapport';
import {
  cleMois,
  estNombre,
  montant,
  pluriel,
  sectionsDeLaPage,
  signalements,
  titreSignalement,
  tranchePartielle,
} from './reportHelpers';

/**
 * Camions loués: par propriétaire, une carte par camion. La barre répartit la
 * recette générée entre les charges payées par la société, le loyer (la part
 * du propriétaire) et la marge qui reste à la société. Quand les frais
 * dépassent la recette, la part qui manque apparaît en rouge.
 *
 * `proprietaires`: nom du propriétaire → identifiant de la société, pour
 * proposer « Voir le relevé » (compte d'exploitation filtré sur ce
 * transporteur). Sans correspondance, le lien n'est pas proposé.
 *
 * Les cartes se lisent par pages (20, 50 ou 100 camions), propriétaire après
 * propriétaire. Un propriétaire coupé entre deux pages reparaît en tête de la
 * suivante; ses chiffres d'en-tête et le TOTAL portent sur tous ses camions,
 * jamais sur la page.
 */

const COULEURS = {
  charges: 'hsl(215 14% 58%)',
  loyer: 'hsl(30 80% 50%)',
  marge: 'hsl(var(--success))',
  manque: 'hsl(var(--destructive))',
};

export const repartition = (camion) => {
  const charges = montant(camion.company_charges);
  const loyer = montant(camion.rent);
  const marge = montant(camion.margin);
  const parts = [
    { key: 'charges', label: 'Charges de la société', valeur: charges, couleur: COULEURS.charges },
    { key: 'loyer', label: 'Fret (part du propriétaire)', valeur: loyer, couleur: COULEURS.loyer },
  ];
  if (marge > 0) parts.push({ key: 'marge', label: 'Marge de la société', valeur: marge, couleur: COULEURS.marge });
  if (marge < 0) parts.push({ key: 'manque', label: 'Frais au-delà de la recette', valeur: -marge, couleur: COULEURS.manque });
  return parts.filter((p) => p.valeur > 0);
};

const PastilleVerdict = ({ rapporte }) =>
  rapporte ? <Badge variant="success">Rapporte</Badge> : <Badge variant="destructive">Ne rapporte pas</Badge>;

const CarteCamion = ({ camion }) => {
  const parts = repartition(camion);
  const alertes = signalements(camion);
  const netProprietaire = camion.owner_net ?? camion.net_owner;
  return (
    <li className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">
            {camion.tractor_plate || 'Camion sans plaque'}
            {camion.tank_plate && <span className="font-normal text-muted-foreground"> · {camion.tank_plate}</span>}
          </p>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {camion.driver_name && <span>{camion.driver_name}</span>}
            <span>
              {camion.rotations || 0} {pluriel(camion.rotations, 'rotation', 'rotations')}
            </span>
            {camion.estimated && <PastillePrevu />}
            {alertes.map((texte) => (
              <PastilleSignalement key={texte} texte={texte} titre={titreSignalement(camion, texte)} />
            ))}
          </p>
        </div>
        <PastilleVerdict rapporte={Boolean(camion.earned)} />
      </div>

      {parts.length > 0 ? (
        <BarreEmpilee parts={parts} hauteur="h-2.5" />
      ) : (
        <p className="text-xs text-muted-foreground">Ni recette ni frais ce mois.</p>
      )}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <Ligne label="Recette générée" valeur={camion.revenue} />
        <Ligne label="Charges de la société" valeur={camion.company_charges} pastille={COULEURS.charges} />
        <Ligne label="Fret (part du propriétaire)" valeur={camion.rent} pastille={COULEURS.loyer} />
        <Ligne label="Marge de la société" valeur={camion.margin} signe fort pastille={montant(camion.margin) > 0 ? COULEURS.marge : undefined} />
        {estNombre(netProprietaire) && <Ligne label="Net dû au propriétaire" valeur={netProprietaire} />}
      </dl>
    </li>
  );
};

const Ligne = ({ label, valeur, signe, fort, pastille }) => (
  <div className="min-w-0">
    <dt className="flex items-center gap-1.5 text-[11px] leading-4 text-muted-foreground">
      {pastille && <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: pastille }} aria-hidden="true" />}
      {label}
    </dt>
    <dd>
      <Montant valeur={valeur} signe={signe} fort={fort} />
    </dd>
  </div>
);

const RentedTrucksReport = ({ report, periode, proprietaires }) => {
  const camions = React.useMemo(() => report?.trucks || [], [report]);
  const totaux = report?.totals || {};

  // Un bloc par propriétaire, dans l'ordre du classement (le meilleur d'abord).
  const parProprietaire = React.useMemo(() => {
    const groupes = new Map();
    camions.forEach((camion) => {
      const nom = camion.lessor_name || 'Propriétaire non précisé';
      if (!groupes.has(nom)) groupes.set(nom, []);
      groupes.get(nom).push(camion);
    });
    return groupes;
  }, [camions]);
  const entrees = React.useMemo(
    () =>
      Array.from(parProprietaire.entries()).flatMap(([nom, liste]) =>
        liste.map((camion, rang) => ({ section: nom, rang, camion })),
      ),
    [parProprietaire],
  );
  const pagination = usePagination(entrees, { cle: 'loues', reinitialiser: report?.period?.label || '' });

  if (!report || camions.length === 0) {
    return (
      <EmptyState
        icon={KeyRound}
        title="Aucun camion loué ce mois-ci"
        description={`Aucun camion pris en location n'a roulé en ${report?.period?.label || 'ce mois'}.`}
      >
        Les camions loués apparaissent ici dès qu'ils ont une rotation ou un fret dans le mois.
      </EmptyState>
    );
  }

  const lienReleve = (nom) => {
    const id = proprietaires?.get?.(nom);
    if (!id) return null;
    const params = new URLSearchParams({ onglet: 'compte', mois: cleMois(periode), transporteur: id });
    return `/rapports?${params.toString()}`;
  };

  const somme = (liste, cle) => liste.reduce((s, c) => s + montant(c[cle]), 0);

  const aRapporte = totaux.earned_count ?? camions.filter((c) => c.earned).length;
  const nb = totaux.trucks_count ?? camions.length;
  const estime = camions.some((camion) => camion.estimated);
  const legende = [
    { key: 'charges', label: 'Charges de la société', couleur: COULEURS.charges },
    { key: 'loyer', label: 'Fret (part du propriétaire)', couleur: COULEURS.loyer },
    { key: 'marge', label: 'Marge de la société', couleur: COULEURS.marge },
    { key: 'manque', label: 'Frais au-delà de la recette', couleur: COULEURS.manque },
  ];

  return (
    <div className="space-y-5">
      <EncartSignalements textes={signalements(report)} />
      <Legende parts={legende} montants={false} />

      {sectionsDeLaPage(pagination.lignes).map(({ cle: nom, entrees: tranche }) => {
        const liste = parProprietaire.get(nom);
        const partiel = tranchePartielle(tranche, liste.length, 'camions');
        const lien = lienReleve(nom);
        const idTitre = `proprietaire-${nom}`.replace(/\s+/g, '-');
        return (
          <section key={nom} aria-labelledby={idTitre} className="space-y-3">
            <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
              <div>
                <h3 id={idTitre} className="text-sm font-semibold text-foreground">
                  {nom}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {partiel ? <span className="font-medium text-foreground">{partiel}</span> : `${liste.length} ${pluriel(liste.length, 'camion', 'camions')}`}
                  {lien && (
                    <>
                      {' · '}
                      <Link to={lien} className="font-medium text-primary underline-offset-4 hover:underline">
                        Voir le relevé
                      </Link>
                    </>
                  )}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Chiffre label="Recettes" valeur={somme(liste, 'revenue')} />
                <Chiffre label="Fret" valeur={somme(liste, 'rent')} />
                <Chiffre label="Marge" valeur={somme(liste, 'margin')} signe fort />
              </div>
            </header>
            <ul className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
              {tranche.map(({ camion }) => (
                <CarteCamion key={camion.truck_id || `${camion.rank}-${camion.tractor_plate}`} camion={camion} />
              ))}
            </ul>
          </section>
        );
      })}

      <BarrePagination pagination={pagination} nom="camions loués" className="mt-0" />

      <section
        aria-label="Total des camions loués"
        className={cn('flex flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded-xl border-2 border-foreground/15 bg-card px-4 py-3 shadow-sm')}
      >
        <p className="text-sm font-semibold">
          TOTAL{' '}
          <span className="text-xs font-normal text-muted-foreground">
            {totaux.rotations ?? 0} {pluriel(totaux.rotations, 'rotation', 'rotations')} ·{' '}
            {`${aRapporte} sur ${nb} ${pluriel(aRapporte, 'a rapporté', 'ont rapporté')}`}
          </span>
        </p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Chiffre label="Recettes" valeur={totaux.revenue ?? 0} />
          <Chiffre label="Charges de la société" valeur={totaux.company_charges ?? 0} />
          <Chiffre label="Fret" valeur={totaux.rent ?? 0} />
          <Chiffre label="Marge de la société" valeur={totaux.margin ?? 0} signe fort />
        </div>
      </section>

      {(report.note || estime) && (
        <p className="text-xs text-muted-foreground">
          {report.note}
          {report.note && estime && ' '}
          {estime && 'Les charges marquées « Prévu » contiennent un montant estimé, pas encore payé.'}
        </p>
      )}
    </div>
  );
};

export default RentedTrucksReport;
